import { apiSlice } from './api-slice';
import {
  Response,
  Sortable,
  Directory,
  Document,
  SavedDirectory,
  DriverData,
} from '../models';
import { PAGINATION_SIZE } from '../constants';

export const documentsApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getDirectoryById: builder.query<Response<Directory>, number>({
      query: directoryId => ({
        url: `company/directory/${directoryId}`,
        method: 'GET',
      }),
      providesTags: (_, __, directoryId) => [
        { type: 'Document', id: directoryId },
      ],
    }),
    getDirectoryByPhaseId: builder.query<
      Response<Sortable<Directory>>,
      { projectId: number; phaseId: number; page: number }
    >({
      query: ({ projectId, phaseId, page }) => ({
        url: `company/directory/fetchByProjectId/${projectId}/phase/${phaseId}`,
        params: { size: PAGINATION_SIZE, page },
        method: 'GET',
      }),
      providesTags: result =>
        result?.data.content.length
          ? result.data.content.map(({ id }) => ({ type: 'Document', id }))
          : ['Document'],
    }),
    createDirectory: builder.mutation<Response<Directory>, SavedDirectory>({
      query: data => ({
        url: 'company/directory',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Document' }],
    }),
    updateDirectory: builder.mutation<
      Response<Directory>,
      Pick<
        SavedDirectory,
        | 'directoryName'
        | 'directoryTags'
        | 'viewRoles'
        | 'editRoles'
        | 'deleteRoles'
      > & { id: number }
    >({
      query: data => ({
        url: 'company/directory',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: result => {
        // TODO: add invalidatesTags coming from data from backend
        return [{ type: 'Document', id: `${result!.data.id}` }];
      },
    }),
    deleteProject: builder.mutation<Response<null>, { projectId: number }>({
      query: ({ projectId }) => ({
        url: `company/project/${projectId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => {
        return [{ type: 'Document' }];
      },
    }),
    uploadDocument: builder.mutation<Response<Document[]>, FormData>({
      query: data => ({
        url: 'company/document/upload',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [{ type: 'Document' }],
    }),
    archiveDriver: builder.mutation<void, DriverData[]>({
      query: data => ({
        url: 'company/driver/archiveDriver',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Document' }],
    }),
    moveDriver: builder.mutation<void, DriverData[]>({
      query: data => ({
        url: 'company/driver/moveDriver',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Document' }],
    }),
    deleteDriver: builder.mutation<void, DriverData[]>({
      query: data => ({
        url: 'company/driver/deleteDriver',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Document' }],
    }),
    getDirectoryTags: builder.query<
      Response<
        {
          id: number;
          name: string;
          directories: [];
        }[]
      >,
      void
    >({
      query: () => ({
        url: `company/driverTag`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetDirectoryByIdQuery,
  useGetDirectoryByPhaseIdQuery,
  useCreateDirectoryMutation,
  useUpdateDirectoryMutation,
  useUploadDocumentMutation,
  useArchiveDriverMutation,
  useMoveDriverMutation,
  useDeleteDriverMutation,
  useGetDirectoryTagsQuery,
} = documentsApiSlice;
